/* eslint-disable no-undef */
/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
  Tooltip,
  Chip,
  IconButton,
  Switch,
  FormControlLabel,
  FormGroup,
  Checkbox,
  RadioGroup,
  Radio,
  FormLabel,
  TextField,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import PropTypes from 'prop-types';
import { useThemeContext } from '../Context/ThemeContext';
import { toast } from 'react-toastify';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LaunchIcon from '@mui/icons-material/Launch';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLocation } from 'react-router-dom';
import {
  fetchWorkflowRunStatus,
  triggerExecuteWorkflow,
  triggerCancelWorkflow,
  fetchScheduledStatus,
  cancelWorkflowSchedule,
} from '../apis/Workflows';

import { useDispatch, useSelector } from 'react-redux';
import {
  saveWorkflow,
  setScheduleDateTime,
  setScheduledStatus,
  setTriggerResponse,
  setScheduleLockUntil,
  setExecutionStatus,
} from '../state/slices/workflowSlice';
import { setBlinkByWorkflow } from '../state/slices/mappingSlice';
import LogsViewer, { SimpleLogsViewer } from './LogsViewer';
import { sendNotification } from '../apis/email';
import Cookies from 'js-cookie';

function ActionButtonsInner(props, ref) {
  const {
    selectedNode,
    workflowId,
    workflowConfigurationId,
    onWorkflowExecutionComplete,
    onSave,
    collapsedBoth: _collapsedBoth,
    onCollapsedChange,
    leftSidebarRef: _leftSidebarRef,
    rightSidebarRef: _rightSidebarRef,
    fullWidth = false,
    onExecutionStart,
  } = props;
  
  const dispatch = useDispatch();
  const workflowsObj = useSelector((state) => state.workflows.workflows || {});
  const workflowData = workflowsObj[workflowId];
  const workflowRunId = workflowData?.workflowRunId;
  const scheduleLockUntil = workflowData?.scheduleLockUntil;
  const scheduledStatus = workflowData?.scheduledStatus;
  const storedRunStatus = useSelector(
    (state) => state.workflows?.executionStatusByWorkflow?.[workflowId],
  );

  const hasUnsavedChanges = useSelector(
    (state) => state.workflows?.unsavedChangesByWorkflow?.[workflowId],
  );

  const mustSaveBeforeAction =
    Boolean(hasUnsavedChanges) || !workflowConfigurationId;

  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);
  const executionScrollRef = useRef(null);

  // Handle scroll detection for fade in/out scrollbar
  useEffect(() => {
    const scrollContainer = executionScrollRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      setIsScrolling(true);
      
      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Hide scrollbar after 1 second of no scrolling
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 1000);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []); // Empty dependency array - only set up once when component mounts
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [frequency, setFrequency] = useState('');
  const [endDate, setEndDate] = useState(null);
  const [recurrenceEnabled, setRecurrenceEnabled] = useState(false);
  const [customDays, setCustomDays] = useState([]);
  const [hourInterval, setHourInterval] = useState(1);
  const { darkMode } = useThemeContext();
  const location = useLocation();

  const [executing, setExecuting] = useState(false);
  const [runStatus, setRunStatus] = useState(() => storedRunStatus || null);
  const [isPolling, setIsPolling] = useState(false);
  const pollingRef = React.useRef(null);
  const scheduledPollingRef = React.useRef(null);
  const postPollTimeoutRef = useRef(null);
  const followUpRef = useRef(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [simpleLogsDialogOpen, setSimpleLogsDialogOpen] = useState(false);
  const triggerData = useSelector(
    (state) => state.workflows?.workflows?.[workflowId]?.triggerResponse,
  );

  const [shouldSaveAfterSchedule, setShouldSaveAfterSchedule] = useState(false);

  const statusRef = useRef(null);
  const scrolledRunRef = useRef(null);

  const executeDisabled =
    mustSaveBeforeAction ||
    executing ||
    runStatus?.status === 'ACTIVE' ||
    runStatus?.status === 'PROCESSING';

  const executeTooltip = (() => {
    if (mustSaveBeforeAction) {
      return workflowConfigurationId
        ? 'Please save your latest changes before executing.'
        : 'Please save the workflow before executing.';
    }
    if (executing) {
      return 'Execute request in progress...';
    }
    if (runStatus?.status === 'ACTIVE' || runStatus?.status === 'PROCESSING') {
      return 'Workflow execution is already in progress.';
    }
    return null;
  })();

  const startScheduledPolling = () => {
    if (scheduledPollingRef.current) return;

    setSelectedDate(null);
    setSelectedTime(null);
    setFrequency('');
    setEndDate(null);
    setRecurrenceEnabled(false);
    setCustomDays([]);
    setHourInterval(1);

    const pollScheduled = async () => {
      try {
        const status = await fetchScheduledStatus(workflowId);
        dispatch(setScheduledStatus({ workflowId, scheduledStatus: status }));

        const runId = status?.latest_run?.workflow_run_id;

        if (runId) {
          if (isPolling) {
            return;
          }

          clearInterval(scheduledPollingRef.current);
          scheduledPollingRef.current = null;

          dispatch(
            saveWorkflow({
              nodes: workflowData?.nodes || [],
              edges: workflowData?.edges || [],
              connectionDetails: workflowData?.connectionDetails || [],
              workflowId: workflowData?.workflowId || workflowId,
              workflowConfigurationId: workflowData?.workflowConfigurationId,
              workflowRunId: runId,
            }),
          );

          fetchStatus(runId);
          setIsPolling(true);
          pollingRef.current = setInterval(() => {
            fetchStatus(runId);
          }, 10000);
        }
      } catch (e) {
        console.warn('Scheduled status polling failed:', e);
      }
    };

    pollScheduled();
    scheduledPollingRef.current = setInterval(pollScheduled, 10000);
  };

  useEffect(() => {
    if (
      shouldSaveAfterSchedule &&
      workflowData?.scheduleDateTime &&
      workflowData?.timezone
    ) {
      if (typeof onSave === 'function') onSave();
      setShouldSaveAfterSchedule(false);
    }
  }, [
    workflowData?.scheduleDateTime,
    workflowData?.timezone,
    shouldSaveAfterSchedule,
  ]);

  useEffect(() => {
    if (!scheduleLockUntil) return;
    const checkLock = () => {
      if (dayjs(scheduleLockUntil).isBefore(dayjs())) {
        dispatch(setScheduleLockUntil({ workflowId, lockUntil: null }));
      }
    };
    checkLock();
    const interval = setInterval(checkLock, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [scheduleLockUntil, workflowId, dispatch]);

  useEffect(() => {
    const terminalStates = ['SUCCESS', 'FAILED', 'PARTIAL_SUCCESS'];
    if (
      scheduledStatus?.status === 'SCHEDULED' &&
      terminalStates.includes((runStatus?.status || '').toUpperCase()) &&
      !scheduledPollingRef.current
    ) {
      startScheduledPolling();
    }
  }, [scheduledStatus?.status, runStatus?.status]);

  useEffect(() => {
    if (storedRunStatus && storedRunStatus !== runStatus) {
      setRunStatus(storedRunStatus);
    }
    if (!storedRunStatus && runStatus && !workflowRunId) {
      setRunStatus(null);
    }
  }, [storedRunStatus, workflowId, workflowRunId]);

  useEffect(() => {
    const scheduledWorkflowId = triggerData?.workflow_id;

    if (triggerData?.status === 'SCHEDULED' && scheduledWorkflowId) {
      startScheduledPolling();
    }

    setSelectedDate(null);
    setSelectedTime(null);
    return () => {
      if (scheduledPollingRef.current) {
        clearInterval(scheduledPollingRef.current);
        scheduledPollingRef.current = null;
      }
    };
  }, [triggerData, dispatch, workflowId]);

  const handleScheduleOpen = () => {
    if (mustSaveBeforeAction) {
      toast.error(
        workflowConfigurationId
          ? 'Please save your latest changes before scheduling.'
          : 'Please save the workflow before scheduling.',
        {
          autoClose: 3000,
        },
      );
      return;
    }
    clearAllTimers();
    setRunStatus(null);
    dispatch(setExecutionStatus({ workflowId, runStatus: null }));
    setSelectedDate(null);
    setSelectedTime(null);
    setFrequency('');
    setEndDate(null);
    setRecurrenceEnabled(false);
    setCustomDays([]);
    setHourInterval(1);
    dispatch(setTriggerResponse({ workflowId, triggerResponse: null }));
    dispatch(setScheduledStatus({ workflowId, scheduledStatus: null }));
    dispatch(
      setScheduleDateTime({
        workflowId,
        scheduleDateTime: null,
        timezone: null,
        frequency: null,
        endDate: null,
        customDays: null,
        hourInterval: null,
      }),
    );

    setScheduleDialogOpen(true);
  };

  const handleScheduleClose = () => {
    setScheduleDialogOpen(false);
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  const handleTimeChange = (newTime) => {
    setSelectedTime(newTime);
  };

  const handleCancelFlow = async () => {
    try {
      await triggerCancelWorkflow(workflowRunId);
      toast.success('Workflow execution cancelled.');

      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      setIsPolling(false);

        dispatch(
          saveWorkflow({
            ...workflowData,
            workflowId: workflowId,
            workflowRunId: null,
          }),
        );

        setRunStatus(null);
        dispatch(setExecutionStatus({ workflowId, runStatus: null }));
      } catch (err) {
        toast.error('Failed to cancel workflow execution.');
        console.error(err);
      }
    };

  const handleCancelSchedule = async () => {
    try {
      await cancelWorkflowSchedule(workflowId);
      toast.success('Schedule cancelled.');

      clearAllTimers();
      setRunStatus(null);
      dispatch(setExecutionStatus({ workflowId, runStatus: null }));
      dispatch(setScheduledStatus({ workflowId, scheduledStatus: null }));
      dispatch(setTriggerResponse({ workflowId, triggerResponse: null }));
      dispatch(
        setScheduleDateTime({
          workflowId,
          scheduleDateTime: null,
          timezone: null,
          frequency: null,
          endDate: null,
          customDays: null,
          hourInterval: null,
        }),
      );
      dispatch(setScheduleLockUntil({ workflowId, lockUntil: null }));
    } catch (err) {
      toast.error('Failed to cancel schedule.');
      console.error(err);
    }
  };

  const handleViewJiraTicket = () => {
    if (runStatus?.jira_ticket_url) {
      window.open(runStatus.jira_ticket_url, '_blank');
    }
  };

  const clearAllTimers = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (postPollTimeoutRef.current) clearTimeout(postPollTimeoutRef.current);
    pollingRef.current = null;
    postPollTimeoutRef.current = null;

    if (scheduledPollingRef.current) {
      clearInterval(scheduledPollingRef.current);
      scheduledPollingRef.current = null;
    }
  };

  const stopPollingAndScheduleFollowUp = (runId) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    setIsPolling(false);

    if (!followUpRef.current) {
      followUpRef.current = true;
      postPollTimeoutRef.current = setTimeout(() => {
        fetchStatus(runId);
      }, 10000);
    }
  };

  const handleCheckExecution = () => {
    if (mustSaveBeforeAction) {
      toast.error(
        workflowConfigurationId
          ? 'Please save your latest changes before executing.'
          : 'Please save the workflow before executing.',
        {
          autoClose: 3000,
        },
      );
      return;
    }
    dispatch(
      setBlinkByWorkflow({ workflowId, button: 'execute', value: false }),
    );
    if (workflowRunId) {
      onWorkflowExecutionComplete(workflowId);
    }

    if (onExecutionStart) {
      console.log('Calling onExecutionStart callback');
      onExecutionStart();
    } else {
      console.log('onExecutionStart callback is not defined');
    }
    setTimeout(() => {
      handleExecute();
    }, 2000);
  };


  const handleExecute = async () => {
    if (mustSaveBeforeAction || !workflowId) {
      toast.error(
        workflowConfigurationId
          ? 'Please save your latest changes before executing.'
          : 'Please save the workflow before executing.',
        {
          autoClose: 3000,
        },
      );
      return;
    }

    clearAllTimers();
    dispatch(setTriggerResponse({ workflowId, triggerResponse: null }));
    dispatch(setScheduledStatus({ workflowId, scheduledStatus: null }));
    followUpRef.current = false;

    setExecuting(true);
    setRunStatus(null);
    dispatch(setExecutionStatus({ workflowId, runStatus: null }));

    try {
      const response = await triggerExecuteWorkflow(workflowId);
      onCollapsedChange?.(true);
      onExecutionStart?.();
      toast.success('Workflow execution triggered.', {
        autoClose: 2000,
      });

      if (response.workflow_run_id) {
        dispatch(
          saveWorkflow({
            nodes: workflowData?.nodes || [],
            edges: workflowData?.edges || [],
            connectionDetails: workflowData?.connectionDetails || [],
            workflowId: workflowData?.workflowId,
            workflowConfigurationId: workflowData?.workflowConfigurationId,
            workflowRunId: response.workflow_run_id,
          }),
        );

        try {
          await sendNotification({
            to_email: Cookies.get('userEmail'),
            subject: 'Workflow Started',
            message: `Workflow ${workflowId} started. Run ID: ${response.workflow_run_id}`,
            workflow_id: workflowId,
            workflow_name: workflowId,
          });
        } catch (e) {
          console.warn('Notification email failed:', e);
        }

        fetchStatus(response.workflow_run_id);
        setIsPolling(true);
        pollingRef.current = setInterval(() => {
          fetchStatus(response.workflow_run_id);
        }, 10000);
        
      }
    } catch (error) {
      toast.error('Failed to trigger execution');
    } finally {
      setExecuting(false);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      runNow: handleCheckExecution,
      runImmediately: handleExecute,
      openSchedule: handleScheduleOpen,
      refreshStatus: () => {
        if (workflowRunId) fetchStatus(workflowRunId);
      },
    }),
    [workflowRunId, mustSaveBeforeAction, onExecutionStart],
  );

  useEffect(() => {
    if (location.state?.runNow) {
      handleCheckExecution();
    }
    window.history.replaceState({}, document.title);
  }, [location.state]);

  const fetchStatus = async (runId) => {
    setIsRefreshing(true);

    const minDuration = 3000;
    const startTime = Date.now();

    try {
      const statusData = await fetchWorkflowRunStatus(runId);
      setRunStatus(statusData);
      dispatch(setExecutionStatus({ workflowId, runStatus: statusData }));

      if (statusRef.current && scrolledRunRef.current !== runId) {
        scrolledRunRef.current = runId;

        setTimeout(() => {
          statusRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 0);
      }

      const terminalStates = ['SUCCESS', 'FAILED', 'PARTIAL_SUCCESS'];
      if (terminalStates.includes(statusData.status?.toUpperCase())) {
        stopPollingAndScheduleFollowUp(runId);
      }
    } catch (err) {
      const errorStatus = {
        status: 'Error fetching status',
        error_message: err.message,
      };
      setRunStatus(errorStatus);
      dispatch(setExecutionStatus({ workflowId, runStatus: errorStatus }));

      stopPollingAndScheduleFollowUp(runId);
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(minDuration - elapsed, 0);
      setTimeout(() => setIsRefreshing(false), remaining);
    }
  };

  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, []);

  return (
    <>
      <Container>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 4,
            mb: 2,
          }}
        >
          {runStatus?.jira_ticket_url && (
            <Button
              variant="outlined"
              sx={{
                borderRadius: 0.5,
                color: darkMode ? '#ffffff' : '#1976d2',
                borderColor: darkMode ? '#64748b' : '#1976d2',
                '&:hover': {
                  backgroundColor: darkMode
                    ? 'rgba(100, 116, 139, 0.1)'
                    : 'rgba(25, 118, 210, 0.04)',
                  borderColor: darkMode ? '#94a3b8' : '#1565c0',
                },
              }}
              onClick={handleViewJiraTicket}
              startIcon={<LaunchIcon />}
            >
              View Jira Ticket
            </Button>
          )}

        </Box>

        <Dialog
          open={scheduleDialogOpen}
          onClose={handleScheduleClose}
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: 2,
              width: 500,
              height: 'auto',
              backgroundColor: darkMode ? '#282828' : '#ffffff',
              color: darkMode ? '#ffffff' : 'inherit',
            },
          }}
        >
          <DialogTitle>Select Date & Time</DialogTitle>
          <DialogContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              justifyContent: 'center',
              alignItems: 'center',
              p: 2,
              overflow: 'visible',
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ width: '100%' }}>
                <DatePicker
                  label="Schedule Date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  minDate={dayjs()}
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      color: darkMode ? '#ffffff' : 'inherit',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: darkMode
                        ? 'rgba(255, 255, 255, 0.23)'
                        : 'rgba(0, 0, 0, 0.23)',
                    },
                    '& .MuiInputLabel-root': {
                      color: darkMode
                        ? 'rgba(255, 255, 255, 0.7)'
                        : 'rgba(0, 0, 0, 0.6)',
                    },
                    '& .MuiSvgIcon-root': {
                      color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                    },
                  }}
                />
              </Box>
              <Box sx={{ width: '100%' }}>
                <TimePicker
                  label="Schedule Time"
                  value={selectedTime}
                  onChange={handleTimeChange}
                  minutesStep={1}
                  timeSteps={{ minutes: 1 }}
                  ampm={false}
                  minTime={
                    selectedDate && dayjs(selectedDate).isSame(dayjs(), 'day')
                      ? dayjs()
                      : dayjs().startOf('day')
                  }
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      color: darkMode ? '#ffffff' : 'inherit',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: darkMode
                        ? 'rgba(255, 255, 255, 0.23)'
                        : 'rgba(0, 0, 0, 0.23)',
                    },
                    '& .MuiInputLabel-root': {
                      color: darkMode
                        ? 'rgba(255, 255, 255, 0.7)'
                        : 'rgba(0, 0, 0, 0.6)',
                    },
                    '& .MuiSvgIcon-root': {
                      color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                    },
                  }}
                />
              </Box>
              <Box sx={{ width: '100%' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={recurrenceEnabled}
                      onChange={(e) => setRecurrenceEnabled(e.target.checked)}
                      inputProps={{ 'aria-label': 'Enable recurrence' }}
                    />
                  }
                  label="Enable Recurrence"
                  sx={{
                    color: darkMode ? '#ffffff' : 'inherit',
                  }}
                />
              </Box>

              {recurrenceEnabled && (
                <Box sx={{ width: '100%' }}>
                  <FormLabel
                    id="recurrence-pattern-label"
                    sx={{
                      fontSize: 14,
                      color: darkMode
                        ? 'rgba(255,255,255,0.7)'
                        : 'rgba(0,0,0,0.6)',
                      mb: 0.5,
                    }}
                  >
                    Recurrence Pattern
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="recurrence-pattern-label"
                    name="recurrence"
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 1,
                    }}
                  >
                    <FormControlLabel
                      value="hourly"
                      control={<Radio />}
                      label="Hourly"
                    />
                    <FormControlLabel
                      value="daily"
                      control={<Radio />}
                      label="Daily"
                    />
                    <FormControlLabel
                      value="weekly"
                      control={<Radio />}
                      label="Weekly"
                    />
                    <FormControlLabel
                      value="monthly"
                      control={<Radio />}
                      label="Monthly"
                    />
                  </RadioGroup>
                </Box>
              )}

              {recurrenceEnabled && frequency === 'hourly' && (
                <Box sx={{ width: '100%' }}>
                  <TextField
                    type="number"
                    label="Repeat every (hours)"
                    value={hourInterval}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '') return setHourInterval('');
                      const num = Number(val);
                      if (!Number.isNaN(num)) {
                        setHourInterval(
                          Math.max(1, Math.min(24, Math.floor(num))),
                        );
                      }
                    }}
                    inputProps={{
                      min: 1,
                      max: 24,
                      step: 1,
                      'aria-label': 'Hourly interval in hours',
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-root': {
                        color: darkMode ? '#ffffff' : 'inherit',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: darkMode
                          ? 'rgba(255, 255, 255, 0.23)'
                          : 'rgba(0, 0, 0, 0.23)',
                      },
                      '& .MuiInputLabel-root': {
                        color: darkMode
                          ? 'rgba(255, 255, 255, 0.7)'
                          : 'rgba(0, 0, 0, 0.6)',
                      },
                    }}
                  />
                </Box>
              )}

              {recurrenceEnabled && frequency === 'weekly' && (
                <Box sx={{ width: '100%' }}>
                  <FormLabel
                    id="days-of-week-label"
                    sx={{
                      fontSize: 14,
                      color: darkMode
                        ? 'rgba(255,255,255,0.7)'
                        : 'rgba(0,0,0,0.6)',
                      mb: 0.5,
                    }}
                  >
                    Select Days
                  </FormLabel>
                  <FormGroup
                    role="group"
                    aria-labelledby="days-of-week-label"
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      gap: 1,
                    }}
                  >
                    {[
                      { key: 'monday', label: 'M' },
                      { key: 'tuesday', label: 'T' },
                      { key: 'wednesday', label: 'W' },
                      { key: 'thursday', label: 'TH' },
                      { key: 'friday', label: 'F' },
                      { key: 'saturday', label: 'S' },
                      { key: 'sunday', label: 'SU' },
                    ].map((d) => (
                      <FormControlLabel
                        key={d.key}
                        control={
                          <Checkbox
                            checked={customDays.includes(d.key)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setCustomDays((prev) => {
                                if (checked) return [...prev, d.key];
                                return prev.filter((x) => x !== d.key);
                              });
                            }}
                            inputProps={{ 'aria-label': d.key }}
                          />
                        }
                        label={d.label}
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}

              <Box sx={{ width: '100%' }}>
                <DatePicker
                  label={frequency ? 'End Date (required)' : 'End Date'}
                  value={endDate}
                  onChange={(d) => setEndDate(d)}
                  disabled={!frequency}
                  minDate={selectedDate || dayjs()}
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      color: darkMode ? '#ffffff' : 'inherit',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: darkMode
                        ? 'rgba(255, 255, 255, 0.23)'
                        : 'rgba(0, 0, 0, 0.23)',
                    },
                    '& .MuiInputLabel-root': {
                      color: darkMode
                        ? 'rgba(255, 255, 255, 0.7)'
                        : 'rgba(0, 0, 0, 0.6)',
                    },
                    '& .MuiSvgIcon-root': {
                      color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                    },
                  }}
                />
              </Box>
            </LocalizationProvider>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={handleScheduleClose}
              sx={{
                color: darkMode ? '#3a8eff' : 'primary.main',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                let runAtLocal = null;
                if (selectedDate && selectedTime) {
                  const datePart = selectedDate.format('YYYY-MM-DD');
                  const timePart = selectedTime.format('HH:mm:ss');
                  runAtLocal = `${datePart}T${timePart}`;
                }
                const timezone =
                  Intl.DateTimeFormat().resolvedOptions().timeZone;

                if (recurrenceEnabled && !frequency) {
                  toast.error('Please select a recurrence pattern.', {
                    autoClose: 3000,
                  });
                  return;
                }
                if (
                  recurrenceEnabled &&
                  frequency === 'weekly' &&
                  customDays.length === 0
                ) {
                  toast.error(
                    'Please select at least one day for weekly recurrence.',
                    {
                      autoClose: 3000,
                    },
                  );
                  return;
                }

                if (frequency && !endDate) {
                  toast.error(
                    'Please select an end date for recurring schedule.',
                    {
                      autoClose: 3000,
                    },
                  );
                  return;
                }

                if (frequency && endDate && selectedDate) {
                  const isBefore = dayjs(endDate).isBefore(
                    dayjs(selectedDate),
                    'day',
                  );
                  if (isBefore) {
                    toast.error('End date cannot be before the start date.', {
                      autoClose: 3000,
                    });
                    return;
                  }
                }

                if (endDate) {
                  const endOfDay = dayjs(endDate).endOf('day');
                  if (!endOfDay.isAfter(dayjs())) {
                    toast.error('End date must be in the future.', {
                      autoClose: 3000,
                    });
                    return;
                  }
                }

                if (recurrenceEnabled && frequency === 'hourly') {
                  const n = Number(hourInterval);
                  if (!Number.isInteger(n) || n < 1 || n > 24) {
                    toast.error('Please enter hours between 1 and 24.', {
                      autoClose: 3000,
                    });
                    return;
                  }
                }

                dispatch(
                  setScheduleDateTime({
                    workflowId: workflowId,
                    scheduleDateTime: runAtLocal,
                    timezone,
                    frequency:
                      recurrenceEnabled && frequency
                        ? String(frequency).toUpperCase()
                        : null,
                    endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
                    customDays:
                      recurrenceEnabled && frequency === 'weekly'
                        ? customDays
                        : null,
                    hourInterval:
                      recurrenceEnabled && frequency === 'hourly'
                        ? Number(hourInterval)
                        : null,
                  }),
                );

                if (endDate) {
                  const lockUntilIso = dayjs(endDate)
                    .endOf('day')
                    .toISOString();
                  if (dayjs(lockUntilIso).isAfter(dayjs())) {
                    dispatch(
                      setScheduleLockUntil({
                        workflowId,
                        lockUntil: lockUntilIso,
                      }),
                    );
                  }
                }

                setRunStatus(null);
                dispatch(setExecutionStatus({ workflowId, runStatus: null }));
                handleScheduleClose();
                setConfirmationOpen(true);

                setShouldSaveAfterSchedule(true);
                setFrequency('');
                setEndDate(null);
                setRecurrenceEnabled(false);
                setCustomDays([]);
                setHourInterval(1);

                try {
                  const status = await fetchScheduledStatus(workflowId);
                  dispatch(
                    setScheduledStatus({ workflowId, scheduledStatus: status }),
                  );
                  startScheduledPolling();
                } catch (e) {
                  console.warn(
                    'Failed to fetch scheduled status after confirmation:',
                    e,
                  );
                }
              }}
              sx={{
                color: darkMode ? '#3a8eff' : 'primary.main',
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      <Box
        sx={{
          width: fullWidth ? '100%' : { xs: '100%', md: 'calc(100% - 265px)' },
          ml: fullWidth ? 0 : { xs: 0, md: '265px' },
          boxSizing: 'border-box',
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Paper
          ref={statusRef}
          elevation={0}
          sx={{
            bgcolor: darkMode ? '#0F172A' : '#ffffff',
            color: darkMode ? '#E2E8F0' : '#1e293b',
            width: '100%',
            boxSizing: 'border-box',
            mx: 0,
            overflow: 'hidden',
            borderRadius: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              bgcolor: '#1a1a1a',
              color: '#ffffff',
              py: 1,
              px: 1.5,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'IBM Plex Sans',
                fontSize: '15px',
                fontWeight: 500,
                lineHeight: '100%',
                color: '#FFFFFF',
              }}
            >
              {scheduledStatus?.status === 'SCHEDULED' && workflowRunId
                ? scheduledStatus?.message
                : workflowRunId
                  ? `Workflow Run ID: ${workflowRunId}`
                  : 'No workflow run has been executed yet.'}
            </Typography>
            {workflowRunId && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Tooltip title="View">
                  <IconButton
                    onClick={() => setLogsDialogOpen(true)}
                    size="small"
                    sx={{
                      color: '#ffffff',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Open in new window">
                  <IconButton
                    onClick={() => setSimpleLogsDialogOpen(true)}
                    size="small"
                    sx={{
                      color: '#ffffff',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <LaunchIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Refresh">
                  <IconButton
                    size="small"
                    onClick={() => fetchStatus(workflowRunId)}
                    disabled={isRefreshing}
                    sx={{
                      color: '#ffffff',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    {isRefreshing ? (
                      <CircularProgress size={18} sx={{ color: '#fff' }} />
                    ) : (
                      <RefreshIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title={executeTooltip || 'Run'}>
                  <span style={{ display: 'inline-flex' }}>
                    <IconButton
                      size="small"
                      onClick={handleCheckExecution}
                      sx={{
                        color: '#ffffff',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                      disabled={executeDisabled}
                    >
                      <PlayArrowIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                {scheduledStatus?.status === 'SCHEDULED' && (
                  <Tooltip title="Cancel Schedule">
                    <IconButton
                      onClick={handleCancelSchedule}
                      size="small"
                      sx={{
                        color: '#ffffff',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      <CancelOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {(runStatus?.status === 'ACTIVE' ||
                  runStatus?.status === 'PROCESSING') && (
                  <Tooltip title="Cancel Flow">
                    <IconButton
                      onClick={handleCancelFlow}
                      size="small"
                      sx={{
                        color: '#ffffff',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      <CancelOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>

          <Box
            ref={executionScrollRef}
            sx={{
              bgcolor: darkMode ? '#0F172A' : '#ffffff',
              p: 1.5,
              minHeight: 150,
              flex: 1,
              overflowY: 'auto',
              scrollBehavior: 'smooth',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: isScrolling ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0)',
                borderRadius: '4px',
                transition: 'background 0.3s ease, opacity 0.3s ease',
                opacity: isScrolling ? 1 : 0,
              },
              '&:hover::-webkit-scrollbar-thumb': {
                background: 'rgba(0, 0, 0, 0.4)',
                opacity: 1,
              },
              // Firefox scrollbar
              scrollbarWidth: 'thin',
              scrollbarColor: isScrolling ? 'rgba(0, 0, 0, 0.4) transparent' : 'rgba(0, 0, 0, 0) transparent',
            }}
          >
            {runStatus?.status && (
              <>
                {(() => {
                  const stepsByPhase = runStatus?.steps_by_phase || {};
                  const loadStep = stepsByPhase?.load;
                  const hasLoadStep = loadStep && loadStep.step_status;
                  const loadStatus = loadStep?.step_status?.toUpperCase();
                  const isPartialSuccess =
                    loadStatus === 'PARTIAL_SUCCESS' ||
                    (loadStep?.success_record_count > 0 &&
                      loadStep?.failure_record_count > 0);

                  if (hasLoadStep && isPartialSuccess) {
                    return (
                      <Box sx={{ mb: 2, px: 0, py: 1.5, bgcolor: '#ffffff' }}>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: '#222222',
                            mb: 1,
                          }}
                        >
                          Load step partially succeeded:
                        </Typography>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                          >
                            <CheckCircleIcon sx={{ color: '#10B981', fontSize: '1.25rem' }} />
                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: '#065F46',
                              }}
                            >
                              {loadStep.success_record_count || 0} Succeeded
                            </Typography>
                          </Box>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                          >
                            <ErrorIcon sx={{ color: '#EF4444', fontSize: '1.25rem' }} />
                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: '#991B1B',
                              }}
                            >
                              {loadStep.failure_record_count || 0} Failed
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  }
                  return null;
                })()}

                <Box sx={{ position: 'relative' }}>
                  <Box sx={{ position: 'relative' }}>
                    {runStatus?.start_time && (
                      <Box sx={{ mb: 1.5, position: 'relative', zIndex: 1 }}>
                        <Box display="flex" alignItems="flex-start" gap={3} flexWrap="wrap" sx={{ position: 'relative' }}>
                          <Typography
                            sx={{
                              fontFamily: 'IBM Plex Sans',
                              fontSize: '14px',
                              fontWeight: 400,
                              lineHeight: '16px',
                              color: '#383838',
                              minWidth: '180px',
                            }}
                          >
                            {dayjs(runStatus.start_time).format(
                              'YYYY-MM-DD HH:mm:ss',
                            )}{' '}
                            UTC
                          </Typography>

                          <Chip
                            label="INFO"
                            size="small"
                            sx={{
                              fontFamily: 'Inter',
                              fontSize: '12px',
                              fontWeight: 600,
                              lineHeight: '16px',
                              textTransform: 'uppercase',
                              bgcolor: '#DBEAFE',
                              color: '#1447E6',
                              height: 20,
                              borderRadius: '4px',
                            }}
                          />

                          <Box sx={{ flex: 1, minWidth: '200px', position: 'relative', minHeight: '100px' }}>
                            <Box
                              sx={{
                                position: 'absolute',
                                left: -15,
                                top: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 2,
                              }}
                            >
                              <InfoOutlinedIcon sx={{ fontSize: 18, color: '#1447E6' }} />
                            </Box>
                            <Typography
                              sx={{
                                fontFamily: 'IBM Plex Sans',
                                fontSize: '14px',
                                fontWeight: 500,
                                lineHeight: '20px',
                                color: '#364153',
                                mb: 1,
                                pl: 3,
                              }}
                            >
                              Workflow execution started
                            </Typography>

                            <Box
                              sx={{
                                position: 'absolute',
                                left: 12,
                                top: '28px',
                                bottom: 0,
                                // width: 2,
                                bgcolor: '#000000',
                                border: '1px solid #000000',
                                zIndex: 0,
                                pointerEvents: 'none',
                              }}
                            />

                            <Box display="flex" flexDirection="column" gap={1.5} mt={1} pl={3}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography
                                  sx={{
                                    fontFamily: 'Inter',
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    lineHeight: '16px',
                                    color: '#90A1B9',
                                  }}
                                >
                                  Flow Status:
                                </Typography>
                                <Chip
                                  label={
                                    runStatus.status === 'PARTIAL_SUCCESS'
                                      ? 'Partial Success'
                                      : runStatus.status === 'SUCCESS'
                                        ? 'Success'
                                        : runStatus.status === 'FAILED'
                                          ? 'Failed'
                                          : runStatus.status
                                  }
                                  size="small"
                                  sx={{
                                    fontFamily: 'Inter',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    lineHeight:
                                      runStatus.status === 'PARTIAL_SUCCESS'
                                        ? '100%'
                                        : '16px',
                                    bgcolor:
                                      runStatus.status === 'PARTIAL_SUCCESS'
                                        ? '#FEF9C2'
                                        : runStatus.status === 'SUCCESS'
                                          ? '#DCFCE7'
                                          : runStatus.status === 'FAILED'
                                            ? '#FEE2E2'
                                            : '#F3F4F6',
                                    color:
                                      runStatus.status === 'PARTIAL_SUCCESS'
                                        ? '#894B00'
                                        : runStatus.status === 'SUCCESS'
                                          ? '#016630'
                                          : runStatus.status === 'FAILED'
                                            ? '#991B1B'
                                            : '#6B7280',
                                    border: `1px solid ${
                                      runStatus.status === 'PARTIAL_SUCCESS'
                                        ? '#F59E0B'
                                        : runStatus.status === 'SUCCESS'
                                          ? '#10B981'
                                          : runStatus.status === 'FAILED'
                                            ? '#EF4444'
                                            : '#D1D5DB'
                                    }`,
                                    height: 24,
                                    borderRadius: '6px',
                                  }}
                                />
                              </Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography
                                  sx={{
                                    fontFamily: 'Inter',
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    lineHeight: '16px',
                                    color: '#90A1B9',
                                  }}
                                >
                                  Run Mode:
                                </Typography>
                                <Chip
                                  label={runStatus.is_scheduled ? 'Scheduled' : 'On Demand'}
                                  size="small"
                                  sx={{
                                    fontFamily: 'Inter',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    lineHeight: '16px',
                                    bgcolor: runStatus.is_scheduled ? '#FEF3C7' : '#DCFCE7',
                                    color: runStatus.is_scheduled ? '#92400E' : '#016630',
                                    border: `1px solid ${runStatus.is_scheduled ? '#F59E0B' : '#10B981'}`,
                                    height: 24,
                                    borderRadius: '6px',
                                  }}
                                />
                              </Box>
                            </Box>

                            <Box display="flex" flexDirection="column" gap={1} mt={1} pl={3}>
                              <Typography
                                sx={{
                                  fontFamily: 'Inter',
                                  fontSize: '12px',
                                  fontWeight: 400,
                                  lineHeight: '16px',
                                  color: '#90A1B9',
                                  '& span': {
                                    fontFamily: 'Inter',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    lineHeight: '16px',
                                    color: '#00A63E',
                                  },
                                }}
                              >
                                Success Count:{' '}
                                <span>{runStatus.success_record_count || 0}</span>
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: 'Inter',
                                  fontSize: '12px',
                                  fontWeight: 400,
                                  lineHeight: '16px',
                                  color: '#90A1B9',
                                  '& span': {
                                    fontFamily: 'Inter',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    lineHeight: '16px',
                                    color: '#E7000B',
                                  },
                                }}
                              >
                                Failed Count:{' '}
                                <span>{runStatus.failure_record_count || 0}</span>
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: 'Inter',
                                  fontSize: '12px',
                                  fontWeight: 400,
                                  lineHeight: '16px',
                                  color: '#90A1B9',
                                  '& span': {
                                    fontFamily: 'Inter',
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    lineHeight: '16px',
                                    color: '#90A1B9',
                                  },
                                }}
                              >
                                Skip Count:{' '}
                                <span>{runStatus.skipped_record_count || 0}</span>
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {(() => {
                      const stepsByPhase = runStatus?.steps_by_phase || {};
                      const phases = ['extract', 'transform', 'load'];
                      const phaseLabels = {
                        extract: 'EXTRACT',
                        transform: 'TRANSFORM',
                        load: 'LOAD',
                      };

                      return phases.map((phase) => {
                        const step = stepsByPhase[phase];
                        if (!step) return null;

                        const status = step.step_status?.toUpperCase() || 'UNKNOWN';
                        const successCount = step.success_record_count || 0;
                        const failedCount = step.failure_record_count || 0;
                        const duration = step.duration_seconds
                          ? `${parseFloat(step.duration_seconds.toFixed(2))} sec`
                          : '0.01 sec';
                        const startTime = step.start_time || runStatus.start_time;

                        return (
                          <Box key={phase} sx={{ mb: 1.5, position: 'relative', zIndex: 1 }}>
                            <Box display="flex" alignItems="flex-start" gap={3} flexWrap="wrap" sx={{ position: 'relative' }}>
                              <Typography
                                sx={{
                                  fontFamily: 'IBM Plex Sans',
                                  fontSize: '14px',
                                  fontWeight: 400,
                                  lineHeight: '16px',
                                  color: '#383838',
                                  minWidth: '180px',
                                }}
                              >
                                {startTime
                                  ? `${dayjs(startTime).format('YYYY-MM-DD HH:mm:ss')} UTC`
                                  : ''}
                              </Typography>

                              <Chip
                                label="INFO"
                                size="small"
                                sx={{
                                  fontFamily: 'Inter',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  lineHeight: '16px',
                                  textTransform: 'uppercase',
                                  bgcolor: '#DBEAFE',
                                  color: '#1447E6',
                                  height: 20,
                                  borderRadius: '4px',
                                }}
                              />

                              <Box sx={{ flex: 1, minWidth: '200px', position: 'relative', minHeight: '100px' }}>
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    left: -15,
                                    top: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 2,
                                  }}
                                >
                                  <InfoOutlinedIcon sx={{ fontSize: 18, color: '#1447E6' }} />
                                </Box>
                                <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ pl: 3 }}>
                                  <PlayArrowIcon
                                    sx={{
                                      fontSize: '1rem',
                                      color: '#3B82F6',
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontFamily: 'IBM Plex Sans',
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      lineHeight: '20px',
                                      color: '#364153',
                                    }}
                                  >
                                    {phaseLabels[phase]} started..
                                  </Typography>
                                </Box>

                                {phase === 'extract' && step.step_name && (
                                  <Typography
                                    sx={{
                                      fontFamily: 'Inter',
                                      fontSize: '12px',
                                      fontWeight: 400,
                                      lineHeight: '16px',
                                      color: '#90A1B9',
                                      mb: 1,
                                      pl: 6,
                                    }}
                                  >
                                    Step Status By Phase: {step.step_name}
                                  </Typography>
                                )}

                                <Box display="flex" flexDirection="column" gap={1} mb={1} pl={6}>
                                  <Typography
                                    sx={{
                                      fontFamily: 'Inter',
                                      fontSize: '12px',
                                      fontWeight: 400,
                                      lineHeight: '16px',
                                      color: '#90A1B9',
                                      '& span': {
                                        fontFamily: 'Inter',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        lineHeight: '16px',
                                        color: '#00A63E',
                                      },
                                    }}
                                  >
                                    Success Count:{' '}
                                    <span>{successCount}</span>
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontFamily: 'Inter',
                                      fontSize: '12px',
                                      fontWeight: 400,
                                      lineHeight: '16px',
                                      color: '#90A1B9',
                                      '& span': {
                                        fontFamily: 'Inter',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        lineHeight: '16px',
                                        color: '#E7000B',
                                      },
                                    }}
                                  >
                                    Failed Count:{' '}
                                    <span>{failedCount}</span>
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontFamily: 'Inter',
                                      fontSize: '12px',
                                      fontWeight: 400,
                                      lineHeight: '16px',
                                      color: '#90A1B9',
                                      '& span': {
                                        fontFamily: 'Inter',
                                        fontSize: '12px',
                                        fontWeight: 400,
                                        lineHeight: '16px',
                                        color: '#90A1B9',
                                      },
                                    }}
                                  >
                                    Duration:{' '}
                                    <span>{duration}</span>
                                  </Typography>
                                </Box>

                                <Box
                                  sx={{
                                    position: 'absolute',
                                    left: 12,
                                    top: 0,
                                    bottom: 0,
                                    // width: 2,
                                    bgcolor: '#000000',
                                    border: '1px solid #000000',
                                    zIndex: 0,
                                    pointerEvents: 'none',
                                  }}
                                />

                                <Box display="flex" alignItems="center" gap={1} pl={6}>
                                  <Typography
                                    sx={{
                                      fontFamily: 'Inter',
                                      fontSize: '12px',
                                      fontWeight: 400,
                                      lineHeight: '16px',
                                      color: '#90A1B9',
                                    }}
                                  >
                                    Status:
                                  </Typography>
                                  <Chip
                                    label={
                                      status === 'PARTIAL_SUCCESS'
                                        ? 'Partial Success'
                                        : status === 'SUCCESS'
                                          ? 'Success'
                                          : status === 'FAILED'
                                            ? 'Failed'
                                            : status
                                    }
                                    size="small"
                                    sx={{
                                      fontFamily: 'Inter',
                                      fontSize: '12px',
                                      fontWeight: 500,
                                      lineHeight:
                                        status === 'PARTIAL_SUCCESS'
                                          ? '100%'
                                          : '16px',
                                      bgcolor:
                                        status === 'PARTIAL_SUCCESS'
                                          ? '#FEF9C2'
                                          : status === 'SUCCESS'
                                            ? '#DCFCE7'
                                            : status === 'FAILED'
                                              ? '#FEE2E2'
                                              : '#F3F4F6',
                                      color:
                                        status === 'PARTIAL_SUCCESS'
                                          ? '#894B00'
                                          : status === 'SUCCESS'
                                            ? '#016630'
                                            : status === 'FAILED'
                                              ? '#991B1B'
                                              : '#6B7280',
                                      border: `1px solid ${
                                        status === 'PARTIAL_SUCCESS'
                                          ? '#F59E0B'
                                          : status === 'SUCCESS'
                                            ? '#10B981'
                                            : status === 'FAILED'
                                              ? '#EF4444'
                                              : '#D1D5DB'
                                      }`,
                                      height: 24,
                                      borderRadius: '6px',
                                    }}
                                  />
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        );
                      });
                    })()}
                  </Box>
                </Box>
              </>
            )}

            {!runStatus?.status && workflowRunId && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 4,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}

            {!workflowRunId && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 4,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: darkMode ? '#94a3b8' : '#6b7280',
                  }}
                >
                  No workflow run has been executed yet.
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      <LogsViewer
        open={logsDialogOpen}
        onClose={() => setLogsDialogOpen(false)}
        workflowRunId={workflowRunId}
      />

      <SimpleLogsViewer
        open={simpleLogsDialogOpen}
        onClose={() => setSimpleLogsDialogOpen(false)}
        workflowRunId={workflowRunId}
      />

      <Dialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {'Schedule Set Successfully'}
        </DialogTitle>
        <DialogContent>
          <Typography id="alert-dialog-description">
            Please save the workflow to start scheduling.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmationOpen(false);
              dispatch(
                setBlinkByWorkflow({
                  workflowId,
                  button: 'execute',
                  value: false,
                }),
              );
              dispatch(
                setBlinkByWorkflow({
                  workflowId,
                  button: 'execute',
                  value: false,
                  }),
                );
            }}
            autoFocus
            sx={{ color: darkMode ? '#3a8eff' : 'primary.main' }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const ActionButtons = forwardRef(ActionButtonsInner);

ActionButtons.propTypes = {
  selectedNode: PropTypes.shape({
    id: PropTypes.string,
  }),
  workflowId: PropTypes.string,
  workflowConfigurationId: PropTypes.string,
  onWorkflowExecutionComplete: PropTypes.func,
  onSave: PropTypes.func,
  onCollapsedChange: PropTypes.func,
  collapsedBoth: PropTypes.bool,
  leftSidebarRef: PropTypes.any,
  rightSidebarRef: PropTypes.any,
  fullWidth: PropTypes.bool,
  onExecutionStart: PropTypes.func,
};

export default ActionButtons;
